/**
 * Calendar View Component
 * Displays requests in a calendar format
 */
import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { formatDate, getDatesInRange } from '../utils/dateHelpers';
import logger from '../utils/logger';

const CalendarView = ({ requests, employees, centers = [], month, year, onDateClick, settings, blackoutDays = [], userCenterId = null }) => {
  const currentDate = new Date(year || new Date().getFullYear(), month || new Date().getMonth(), 1);
  const firstDay = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1).getDay();
  const daysInMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0).getDate();
  const [selectedDate, setSelectedDate] = useState(null);
  const [hoveredDate, setHoveredDate] = useState(null);
  
  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  
  // Get blackout days - prioritize center-specific, then prop
  // Note: No longer using global settings.blackoutDays (now managed per center)
  const effectiveBlackoutDays = useMemo(() => {
    // First, check if userCenterId is provided and get center-specific blackout days
    if (userCenterId) {
      const userCenter = centers.find(c => c.id === userCenterId);
      if (userCenter && userCenter.blackoutDays && userCenter.blackoutDays.length > 0) {
        return userCenter.blackoutDays;
      }
    }
    
    // Fall back to prop blackoutDays (if passed directly)
    if (blackoutDays && blackoutDays.length > 0) {
      return blackoutDays;
    }
    
    return [];
  }, [blackoutDays, centers, userCenterId]);
  
  // Create a map of dates to requests (exclude cancelled and rejected)
  const dateRequestMap = useMemo(() => {
    const map = {};
    requests.forEach(request => {
      if (['pending', 'approved', 'cancellation_requested'].includes(request.status)) {
        const dates = getDatesInRange(request.startDate, request.endDate);
        dates.forEach(date => {
          if (!map[date]) {
            map[date] = [];
          }
          map[date].push(request);
        });
      }
    });
    return map;
  }, [requests]);
  
  logger.debug('CalendarView rendered', {
    month: currentDate.getMonth(),
    year: currentDate.getFullYear(),
    requestsCount: requests.length,
  });
  
  const getRequestCount = (date) => {
    const dateStr = formatDate(date, 'YYYY-MM-DD');
    return dateRequestMap[dateStr]?.length || 0;
  };
  
  const getRequestStatus = (date) => {
    const dateStr = formatDate(date, 'YYYY-MM-DD');
    const dateRequests = dateRequestMap[dateStr] || [];
    if (dateRequests.some(r => r.status === 'approved')) {
      return 'approved';
    }
    if (dateRequests.some(r => r.status === 'cancellation_requested')) {
      return 'cancellation_requested';
    }
    if (dateRequests.some(r => r.status === 'pending')) {
      return 'pending';
    }
    return null;
  };
  
  const isBlackoutDay = (dateStr) => {
    return effectiveBlackoutDays.includes(dateStr);
  };
  
  const getRequestsForDate = (dateStr) => {
    return dateRequestMap[dateStr] || [];
  };
  
  const getEmployeeName = (employeeId) => {
    const employee = employees?.find(emp => emp.id === employeeId);
    return employee?.name || 'Unknown';
  };
  
  const handleDateClick = (dateStr) => {
    const dateRequests = getRequestsForDate(dateStr);
    if (dateRequests.length > 0 || isBlackoutDay(dateStr)) {
      setSelectedDate(selectedDate === dateStr ? null : dateStr);
    }
    if (onDateClick) {
      onDateClick(dateStr);
    }
  };
  
  const renderDay = (day) => {
    const date = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
    const dateStr = formatDate(date, 'YYYY-MM-DD');
    const count = getRequestCount(date);
    const status = getRequestStatus(date);
    const isToday = formatDate(new Date(), 'YYYY-MM-DD') === dateStr;
    const isBlackout = isBlackoutDay(dateStr);
    const dateRequests = getRequestsForDate(dateStr);
    
    return (
      <div
        key={day}
        className={`
          min-h-20 p-2 border border-gray-200 cursor-pointer transition-all relative
          ${isToday ? 'bg-blue-50 border-blue-300' : ''}
          ${status === 'approved' ? 'bg-green-50' : ''}
          ${status === 'pending' ? 'bg-yellow-50' : ''}
          ${status === 'cancellation_requested' ? 'bg-orange-50 border-orange-200' : ''}
          ${isBlackout ? 'bg-red-100 border-red-300' : ''}
          ${selectedDate === dateStr ? 'ring-2 ring-primary-500 ring-offset-2' : ''}
          hover:shadow-md
        `}
        onClick={() => handleDateClick(dateStr)}
        onMouseEnter={() => setHoveredDate(dateStr)}
        onMouseLeave={() => setHoveredDate(null)}
      >
        <div className="flex items-center justify-between mb-1">
          <div className={`text-sm font-medium ${isBlackout ? 'text-red-700' : 'text-gray-700'}`}>
            {day}
          </div>
          {isBlackout && (
            <svg className="w-3 h-3 text-red-600" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
          )}
        </div>
        {count > 0 && (
          <div className="text-xs text-gray-600 mb-1">
            {count} request{count > 1 ? 's' : ''}
          </div>
        )}
        {isBlackout && count === 0 && (
          <div className="text-xs text-red-600 font-semibold">Blackout</div>
        )}
        {hoveredDate === dateStr && dateRequests.length > 0 && (
          <div className="absolute z-50 bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-72 bg-white border border-gray-300 rounded-lg shadow-xl p-3 text-xs pointer-events-none">
            <div className="font-semibold text-gray-800 mb-2">{formatDate(dateStr, 'MMM DD, YYYY')}</div>
            {dateRequests.map(req => (
              <div key={req.id} className="mb-2 pb-2 border-b border-gray-200 last:border-0">
                <div className="flex items-center justify-between mb-1">
                  <span className="font-medium text-gray-700">{getEmployeeName(req.employeeId)}</span>
                  <span className={`px-2 py-0.5 rounded text-xs font-semibold ${
                    req.status === 'approved' ? 'bg-green-100 text-green-700' :
                    req.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                    'bg-gray-100 text-gray-700'
                  }`}>
                    {req.status}
                  </span>
                </div>
                <div className="text-gray-600">
                  {formatDate(req.startDate)} - {formatDate(req.endDate)}
                </div>
                {req.reason && (
                  <div className="text-gray-500 mt-1 line-clamp-2">{req.reason}</div>
                )}
              </div>
            ))}
            <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-full w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-300"></div>
          </div>
        )}
      </div>
    );
  };
  
  return (
    <div className="glass p-4">
      <div className="mb-4">
        <h2 className="text-xl font-bold text-gray-800">
          {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
        </h2>
      </div>
      
      <div className="grid grid-cols-7 gap-1">
        {dayNames.map(day => (
          <div key={day} className="text-center text-sm font-semibold text-gray-600 py-2">
            {day}
          </div>
        ))}
        
        {Array.from({ length: firstDay }).map((_, i) => (
          <div key={`empty-${i}`} className="min-h-20 border border-transparent" />
        ))}
        
        {Array.from({ length: daysInMonth }, (_, i) => i + 1).map(day => renderDay(day))}
      </div>
      
      {/* Selected Date Details */}
      {selectedDate && (
        <div className="mt-4 bg-gray-50 rounded-lg p-4 border border-gray-200">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-gray-800">
              {formatDate(selectedDate, 'MMMM DD, YYYY')}
            </h3>
            <button
              onClick={() => setSelectedDate(null)}
              className="text-gray-500 hover:text-gray-700"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          {isBlackoutDay(selectedDate) && (
            <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center space-x-2 text-red-700">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                <span className="font-semibold">Blackout Day - Requests not allowed on this date</span>
              </div>
            </div>
          )}
          {getRequestsForDate(selectedDate).length > 0 ? (
            <div className="space-y-3">
              {getRequestsForDate(selectedDate).map(req => (
                <div key={req.id} className="glass p-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-semibold text-gray-800">{getEmployeeName(req.employeeId)}</span>
                    <span className={`px-2 py-1 rounded text-xs font-semibold ${
                      req.status === 'approved' ? 'bg-green-100 text-green-700' :
                      req.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                      'bg-gray-100 text-gray-700'
                    }`}>
                      {req.status.toUpperCase()}
                    </span>
                  </div>
                  <div className="text-sm text-gray-600 mb-1">
                    <span className="font-medium">Period:</span> {formatDate(req.startDate)} - {formatDate(req.endDate)}
                  </div>
                  <div className="text-sm text-gray-600 mb-1">
                    <span className="font-medium">Duration:</span> {req.days || 1} day{req.days !== 1 ? 's' : ''}
                  </div>
                  {req.reason && (
                    <div className="text-sm text-gray-600 mt-2 pt-2 border-t border-gray-200">
                      <span className="font-medium">Reason:</span> {req.reason}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : !isBlackoutDay(selectedDate) && (
            <div className="text-center py-4 text-gray-500">
              No requests for this date
            </div>
          )}
        </div>
      )}
      
      <div className="mt-4 flex flex-wrap gap-4 text-xs">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-green-50 border border-green-300 rounded"></div>
          <span>Approved</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-yellow-50 border border-yellow-300 rounded"></div>
          <span>Pending</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-orange-50 border border-orange-300 rounded"></div>
          <span>Cancellation Requested</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-blue-50 border border-blue-300 rounded"></div>
          <span>Today</span>
        </div>
        {effectiveBlackoutDays.length > 0 && (
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-red-100 border border-red-300 rounded"></div>
            <span>Blackout Day</span>
          </div>
        )}
      </div>
    </div>
  );
};

CalendarView.propTypes = {
  requests: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      startDate: PropTypes.string.isRequired,
      endDate: PropTypes.string.isRequired,
      status: PropTypes.string.isRequired,
      employeeId: PropTypes.string,
      reason: PropTypes.string,
      days: PropTypes.number,
    })
  ).isRequired,
  employees: PropTypes.array,
  centers: PropTypes.array,
  month: PropTypes.number,
  year: PropTypes.number,
  onDateClick: PropTypes.func,
  settings: PropTypes.object,
  blackoutDays: PropTypes.arrayOf(PropTypes.string),
  userCenterId: PropTypes.string,
};

export default CalendarView;

